import { MaterialCommunityIcons } from '@expo/vector-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    GestureResponderEvent,
    Modal,
    Platform,
    ScrollView,
    TouchableOpacity,
    View
} from 'react-native';
import { Checkbox, Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { useRoleHex } from '../../themes/useRoleHex';
import { DebouncedTextInput } from '../forms';
import { filterStyles as styles } from './filterStyle';

export type EditableItem = {
  id: string;
  name: string;
};

export type EditableItemFilterProps = {
  items: EditableItem[];
  selectedIds: (string | undefined)[];
  onItemToggle: (itemId: string, value: (string | undefined)[], onChange: (value: (string | undefined)[]) => void) => void;
  onChange: (value: (string | undefined)[]) => void;
  editingItemId: string | null;
  editingItemName: string;
  onEditItem: (item: EditableItem, event?: GestureResponderEvent) => void;
  onEditNameChange: (name: string) => void;
  onDeleteItem: () => void;
  onConfirmDelete: () => void;
  confirmDeleteVisible: boolean;
  onConfirmModalClose: () => void;
  onCloseEditMenu: () => void;
  anchorPos: { x: number; y: number } | null;
  itemRefs: React.MutableRefObject<Record<string, any>>;
  searchComponent: React.ReactNode;
  editTitle: string;
  deleteButtonText: string;
  confirmTextLine1: string;
  confirmTextLine2: string;
  yesText: string;
  cancelText: string;
  itemCount: number;
};

export const EditableItemFilter: React.FC<EditableItemFilterProps> = ({
  items,
  selectedIds,
  onItemToggle,
  onChange,
  editingItemId,
  editingItemName,
  onEditItem,
  onEditNameChange,
  onDeleteItem,
  onConfirmDelete,
  confirmDeleteVisible,
  onConfirmModalClose,
  onCloseEditMenu,
  anchorPos,
  itemRefs,
  searchComponent,
  editTitle,
  deleteButtonText,
  confirmTextLine1,
  confirmTextLine2,
  yesText,
  cancelText,
  itemCount,
}) => {
  const { t } = useTranslation();
  const isWeb = Platform.OS === 'web';
  // The Checkbox's checked colour must be a parseable hex, not a var(): Paper
  // runs it through Color(checkedColor).fade(0.32) during render, which throws
  // on a var() and takes the panel down through the error boundary.
  // `rippleColor` does not help - Checkbox.Item applies that to the row's
  // TouchableRipple, never to the inner Checkbox. See `useRoleHex`.
  const selectionCheckedColor = useRoleHex('textStrong');

  const renderItems = () => (
    <View>
      {searchComponent}
      <View style={styles.markersContainer}>
        {items?.map((item: EditableItem, index: number) => (
          <View key={item.id}>
            <View style={[styles.markerItemRow]}>
              <View style={styles.markerLeft}>
                <Text style={styles.markerLabel}>{item.name}</Text>
              </View>
              <View style={styles.markerRight}>
                {isWeb && (
                  <TouchableOpacity
                    ref={(r: any) => { itemRefs.current[item.id] = r; }}
                    onPress={(e?: GestureResponderEvent) => onEditItem(item, e)}
                    style={{ paddingHorizontal: 8 }}
                  >
                    <MaterialCommunityIcons name="dots-horizontal" size={24} color={Colors.role.textSecondary} />
                  </TouchableOpacity>
                )}

                <Checkbox.Item
                  label=""
                  status={selectedIds?.includes(item.id) ? 'checked' : 'unchecked'}
                  style={[
                    {
                      borderWidth: 0,
                      padding: 0,
                    },
                  ]}
                  onPress={() => onItemToggle(item.id, selectedIds || [], onChange)}
                  rippleColor={isWeb ? 'transparent' : undefined}
                  uncheckedColor={Colors.role.fillMuted}
                  color={selectionCheckedColor}
                  mode="android"
                />
              </View>
            </View>
            {index < items.length - 1 && (
              <View
                style={{
                  borderBottomWidth: 1,
                  borderColor: Colors.role.borderControl,
                  marginHorizontal: 12,
                }}
              />
            )}
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <ScrollView
      contentContainerStyle={styles.scrollViewContent}
      scrollEnabled={true}
      showsVerticalScrollIndicator={true}
    >
      {renderItems()}

      {/* flyout menu overlay */}
      {editingItemId && anchorPos && isWeb && (
        <Modal
          visible={!!(editingItemId && anchorPos && isWeb)}
          transparent
          animationType="none"
          onRequestClose={onCloseEditMenu}
        >
          <View pointerEvents="box-none" style={{ flex: 1 }}>
            <TouchableOpacity
              activeOpacity={1}
              onPress={onCloseEditMenu}
              style={styles.editItemModalOverlay}
            />
            <View style={{ ...styles.editItemModal, top: anchorPos.y, left: anchorPos.x }}>
              <View style={styles.editItemModalContainer}>
                <View style={styles.editItemTextContainer}>
                  <Text style={styles.editItemTitle}>{editTitle}</Text>
                  <DebouncedTextInput
                    value={editingItemName}
                    placeholder={editTitle}
                    outlineStyle={styles.editItemInput}
                    autoFocus={true}
                    returnKeyType="done"
                    debounceTimeMs={1000}
                    debounceLength={0}
                    onSubmitEditing={onCloseEditMenu}
                    onChangeText={onEditNameChange}
                    debounceFunc={onEditNameChange}
                    onBlur={() => {}}
                  />
                </View>
                <View style={styles.editItemFooter}>
                  <TouchableOpacity onPress={onDeleteItem}>
                    <Text style={styles.editItemDeleteButton}>
                      <MaterialCommunityIcons name="trash-can-outline" size={16} />
                      {deleteButtonText}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </Modal>
      )}
      {/* End of fly out menu */}

      {/* delete confirmation modal */}
      {editingItemId && (
        <Modal
          visible={confirmDeleteVisible}
          transparent
          animationType="fade"
          onRequestClose={onConfirmModalClose}
        >
          <View style={styles.confirmOverlay}>
            <View style={styles.confirmContainer}>
              <Text style={styles.confirmBody}>
                {confirmTextLine1}
                {"\n\n"}
                {confirmTextLine2}
              </Text>
              <View style={styles.confirmActions}>
                <TouchableOpacity
                  style={styles.confirmYes}
                  onPress={onConfirmDelete}
                >
                  <Text style={styles.confirmYesText}>{yesText}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.confirmCancel}
                  onPress={onConfirmModalClose}
                >
                  <Text style={styles.confirmCancelText}>{cancelText}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      )}
      {/* End of delete confirmation modal */}
    </ScrollView>
  );
};
